import React from 'react'
import { Modal, Table, Badge } from 'react-bootstrap'

const API = () => <Badge bg="info" className="me-1" style={{ fontSize: '0.7rem' }}>API</Badge>
const CALC = () => <Badge bg="secondary" className="me-1" style={{ fontSize: '0.7rem' }}>CALC</Badge>

function MonthlyProfitabilityGuideModal({ show, onHide }) {
    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Monthly Profitability &mdash; Column Reference</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted mb-3">
                    <API /> Value from Amazon API (may be aggregated)
                    &nbsp;&nbsp;
                    <CALC /> Calculated by the system
                </p>

                <h6 className="mb-2">Data Source</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                    Uses pre-collected monthly DataKiosk economics records when available.
                    Falls back to aggregating daily records for the month.
                    Per-ASIN ad spend comes from the monthly ad summary or daily ad performance records and covers
                    Sponsored Products only. Sponsored Brands and Sponsored Display spend is collected per campaign and
                    cannot be tied to an ASIN, so it appears only in the Account Summary.
                </p>

                <h6 className="mb-2">Fulfillment Channel</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                    The Amazon / Seller / Both selector (default Amazon) picks the fulfillment channel for Units Sold and
                    Revenue, taken from the All Orders report (order date in Pacific time, Amazon.com sales channel only),
                    which matches the Seller Central Sales Dashboard. In Amazon or Seller mode only SKUs that sold through
                    that channel in the month are listed. Fees, refunds, COGS per unit and ad spend are per SKU and stay
                    whole, so a SKU that sold through both channels in the same month carries its full fees in either mode.
                    &ldquo;Both&rdquo; is the DataKiosk economics view. If orders data has not been collected for the month,
                    both channels are shown and a warning appears.
                </p>

                <h6 className="mb-2">Per Unit vs Totals</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                    <strong>Per Unit</strong> shows fees and profit for one unit sold, used for pricing and bidding decisions.
                    <strong> Totals</strong> shows the dollars Amazon actually charged for the month, used for the monthly P&amp;L.
                    Per-unit fee &times; units sold will not equal the fee total: per-unit values divide each fee by
                    Amazon&apos;s fee quantity (units the fee was charged on, e.g. units placed or stored), while totals are the
                    charged amounts for the period.
                </p>

                <h6 className="mb-2">Column Definitions</h6>
                <Table size="sm" bordered hover>
                    <thead>
                        <tr>
                            <th>Column</th>
                            <th style={{ width: '90px', textAlign: 'center' }}>Source</th>
                            <th>Calculation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="table-active"><td colSpan="3"><strong>Identity</strong></td></tr>
                        <tr>
                            <td>ASIN</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>From DataKiosk economics records for the selected month</td>
                        </tr>
                        <tr>
                            <td>SKU</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Amazon SKU from economics data</td>
                        </tr>
                        <tr>
                            <td>Product Name</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>From SKU Mapping <code>adv_alias</code>; fallback to FBA inventory product name, then SKU Mapping name</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Pricing &amp; Fees (per unit)</strong></td></tr>
                        <tr>
                            <td>Sale Price</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>revenue / units_sold</code>; fallback to FBA inventory <code>your_price</code></td>
                        </tr>
                        <tr>
                            <td>FBA Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit gross <code>FbaFulfilment</code> fee from DataKiosk (includes base fulfillment + fuel surcharge)</td>
                        </tr>
                        <tr>
                            <td>Inbound Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>FbaInboundConvenience</code> (placement) + <code>FbaInboundTransportation</code> fee</td>
                        </tr>
                        <tr>
                            <td>Referral Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>Referral</code> fee from DataKiosk economics records</td>
                        </tr>
                        <tr>
                            <td>Storage Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>FbaStorage</code> + <code>LongTermStorage</code> + <code>STARStorage</code> fee</td>
                        </tr>
                        <tr>
                            <td>AWD Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>AmazonUpstreamStorage</code> + <code>AmazonUpstreamOutbound</code> fee (AWD warehouse)</td>
                        </tr>
                        <tr>
                            <td>Deal Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>DealParticipation</code> + <code>DealPerformance</code> fee (Lightning Deals, coupons)</td>
                        </tr>
                        <tr>
                            <td>COGS</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>From DataKiosk <code>costOfGoodsSold</code>; fallback to latest receiving cost from SKU Mapping stock item</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Profitability</strong></td></tr>
                        <tr>
                            <td>Profit</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>sale_price &minus; (cogs + fba_fee + inbound_fee + referral_fee + storage_fee + awd_fee + deal_fee + ad_spend_per_unit)</code></td>
                        </tr>
                        <tr>
                            <td>Margin %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(profit / sale_price) &times; 100</code><br /><small className="text-muted">Standard profit margin &mdash; what percentage of the selling price is profit</small></td>
                        </tr>
                        <tr>
                            <td>BE ACoS %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>((sale_price &minus; cost_before_ad) / sale_price) &times; 100</code><br /><small className="text-muted">cost_before_ad = cogs + all fees (excl. ad spend)</small></td>
                        </tr>
                        <tr>
                            <td>Max CPA</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>sale_price &minus; cost_before_ad</code> (contribution margin in dollars &mdash; max you can spend per acquisition)</td>
                        </tr>
                        <tr>
                            <td>Return on Cost %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(profit / cogs) &times; 100</code><br /><small className="text-muted">Profit relative to cost of goods &mdash; higher = more return per dollar of product cost</small></td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Sales</strong></td></tr>
                        <tr>
                            <td>Units Sold</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Total <code>unitsOrdered</code> from DataKiosk economics for the month</td>
                        </tr>
                        <tr>
                            <td>Units Returned</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Total <code>unitsRefunded</code> from DataKiosk economics for the month</td>
                        </tr>
                        <tr>
                            <td>Revenue</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Total <code>orderedProductSales</code> from DataKiosk economics for the month</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Totals view (monthly dollars)</strong></td></tr>
                        <tr>
                            <td>Revenue / Refunded Sales</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td><code>orderedProductSales</code> and <code>refundedProductSales</code> from DataKiosk economics for the month</td>
                        </tr>
                        <tr>
                            <td>FBA, Referral, Inbound, Storage, AWD, Deal Fee</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>
                                Sum of Amazon&apos;s charged amounts for the month by exact fee type
                                <br /><small className="text-muted">FBA = FbaFulfilmentFee; Referral = ReferralFee; Inbound = FbaInboundConvenienceFee + FbaInboundTransportationFee; Storage = FbaStorageFee + LongTermStorageFee + STARStorageFee; AWD = AmazonUpstream* fees; Deal = DealParticipationFee + DealPerformanceFee</small>
                            </td>
                        </tr>
                        <tr>
                            <td>SP Ad Spend / SP Ad Sales / ACoS % / TACoS %</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Sponsored Products spend and attributed sales for the month; <code>ACoS = ad_spend / ad_sales</code>; <code>TACoS = ad_spend / revenue</code>. Per-ASIN columns never include Sponsored Brands / Display spend</td>
                        </tr>
                        <tr>
                            <td>COGS</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>cogs_per_unit &times; units_sold</code></td>
                        </tr>
                        <tr>
                            <td>Net from Amazon</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>revenue &minus; (FBA + referral + inbound + storage + AWD + deal fees) &minus; SP ad spend</code></td>
                        </tr>
                        <tr>
                            <td>Profit / Margin % / Return on Cost %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>
                                <code>profit = net_from_amazon &minus; COGS</code>; <code>margin = profit / revenue</code>; <code>return_on_cost = profit / COGS</code>
                                <br /><small className="text-muted">Refunds, reimbursements, refund fees, removal/disposal/liquidation fees and coupon fees are not deducted; they are shown as separate columns and summary lines</small>
                            </td>
                        </tr>
                        <tr>
                            <td>Refund Fees, Reimbursements, Removal Fees, Coupon Fees, Other Fees</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Memo columns, hidden by default. Other Fees collects any fee type not listed above so nothing is dropped</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Account Summary</strong></td></tr>
                        <tr>
                            <td>Sales, Amazon fees, Not included in profit</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>
                                Sums of the Totals columns over the rows currently shown (search and &ldquo;Exclude items with no activity&rdquo; apply).
                                <br /><small className="text-muted">&ldquo;Exclude items with no activity&rdquo; keeps items that had fees or ad spend without sales, so storage and removal fees on unsold stock are included</small>
                            </td>
                        </tr>
                        <tr>
                            <td>Sponsored Brands / Display spend, Brand + Display spend, Total ad spend</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>
                                Account-level campaign spend from the Sponsored Brands and Sponsored Display campaign reports (settled monthly report when available, otherwise the sum of daily rows).
                                <code>Brand + Display = SB + SD</code>; <code>Total ad spend = SP + SB + SD</code>; <code>TACoS (all ads) = total ad spend / revenue</code>.
                                <br /><small className="text-muted">Blank when the product has not been collected for the month, and while a search is active (account-level spend cannot be split by the searched ASINs). Only spend is combined across products: SP uses 7-day attribution, SB and SD use 14-day, so no all-ads ACoS is shown</small>
                            </td>
                        </tr>
                        <tr>
                            <td>Profit / Margin % / Return on cost % (SP ads vs all ads)</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>
                                <code>Profit (SP ads)</code> is the sum of the per-ASIN Profit column. <code>Profit (all ads) = Profit (SP ads) &minus; Brand + Display spend</code>.
                                Margin % and Return on cost % are weighted (total profit / total revenue or COGS), not averages of row percentages.
                                ASINs without COGS are included in Revenue, fees and Net from Amazon but excluded from COGS, Profit, Margin % and Return on cost %.
                            </td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Inventory</strong></td></tr>
                        <tr>
                            <td>Inventory Level</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>
                                <code>month_supply = afn_total_quantity / ((units_sold / days_in_month) &times; 30)</code>
                                <br /><small className="text-muted">0 &rarr; Out of stock; &lt; <code>inventory_low_stock_months</code> (default 2) &rarr; Low stock; else Enough</small>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </Modal.Body>
        </Modal>
    )
}

export default MonthlyProfitabilityGuideModal
